import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, BACKEND_URL, buildWsUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { X, QrCode, CheckCircle, Camera as CameraIcon } from "@phosphor-icons/react";

/**
 * PhotoSyncPanel
 * Generates a session token, displays a QR code linking to the mobile capture page,
 * and listens to a WebSocket to receive the captured photo in real time.
 */
export default function PhotoSyncPanel({ registrationId, onAttached, onClose }) {
  const [session, setSession] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [status, setStatus] = useState("waiting"); // waiting | received | saving | done
  const [saving, setSaving] = useState(false);
  const wsRef = useRef(null);

  const startSession = useCallback(async () => {
    try {
      const { data } = await api.post("/photo-sync/session");
      setSession(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to start session");
    }
  }, []);

  useEffect(() => {
    startSession();
  }, [startSession]);

  // Open WebSocket once we have a session token
  useEffect(() => {
    if (!session?.token) return;
    const url = buildWsUrl(`/api/ws/photo-sync/${session.token}`);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      // ready
    };
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "photo" && msg.photo_base64) {
          setPhoto(msg.photo_base64);
          setStatus("received");
          toast.success("Photo received from device");
        }
      } catch (e) {
        // ignore
      }
    };
    ws.onerror = () => {
      // do not toast loudly; allow user to retry
    };

    return () => {
      try {
        ws.close();
      } catch {}
    };
  }, [session?.token]);

  const mobileUrl = session ? `${window.location.origin}/m/photo/${session.token}` : "";

  const attachToRegistration = async () => {
    if (!photo || !registrationId) return;
    setSaving(true);
    try {
      await api.post(`/registrations/${registrationId}/photo`, { photo_base64: photo });
      toast.success("Photo saved to applicant");
      setStatus("done");
      onAttached?.(photo);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to save photo");
    } finally {
      setSaving(false);
    }
  };

  const restart = async () => {
    setPhoto(null);
    setStatus("waiting");
    setSession(null);
    await startSession();
  };

  return (
    <div className="border border-slate-900 bg-white p-6 sms-fade-up" data-testid="photo-sync-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <QrCode size={18} weight="duotone" className="text-[var(--sms-primary)]" />
          <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Mobile Photo Sync</div>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="photo-sync-close">
            <X size={18} weight="bold" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* QR side */}
        <div className={`p-6 flex flex-col items-center justify-center text-center ${status === "received" ? "sms-qr-success" : "sms-qr-waiting"}`}>
          {!session && (
            <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-slate-500">Generating session…</div>
          )}
          {session && status !== "received" && (
            <>
              <div className="bg-white p-3 border border-slate-900">
                <QRCodeSVG value={mobileUrl} size={180} level="M" data-testid="photo-qr-code" />
              </div>
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-600 mt-4">
                Scan with phone camera
              </div>
              <div className="text-[10px] font-mono-sms text-slate-400 mt-2 break-all max-w-[220px]" data-testid="photo-mobile-url">
                {mobileUrl}
              </div>
            </>
          )}
          {status === "received" && (
            <>
              <CheckCircle size={48} weight="duotone" className="text-[var(--sms-success)] mb-3" />
              <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-success)]">Photo received</div>
            </>
          )}
        </div>

        {/* Preview side */}
        <div className="p-6 border border-slate-200">
          <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
            Preview
          </div>
          <div className="aspect-square w-full bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
            {photo ? (
              <img src={photo} alt="captured" className="w-full h-full object-cover" data-testid="photo-preview" />
            ) : (
              <div className="text-center text-slate-400">
                <CameraIcon size={36} weight="duotone" className="mx-auto mb-2" />
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em]">Awaiting capture</div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2">
            {photo && status !== "done" && (
              <button onClick={attachToRegistration} disabled={saving || !registrationId} data-testid="btn-attach-photo" className="sms-btn-primary w-full">
                {saving ? "Saving…" : "Save to applicant"}
              </button>
            )}
            <button onClick={restart} data-testid="btn-restart-sync" className="sms-btn-secondary w-full text-xs">
              Restart sync
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 text-[10px] font-mono-sms uppercase tracking-[0.15em] text-slate-400">
        Backend · {BACKEND_URL?.replace(/^https?:\/\//, "")}
      </div>
    </div>
  );
}
